/**
 * 商談詳細上部の「次のアクション」バー。
 * useNextActions が返す配列を、優先度色付きで最大 3 件表示。
 * 4件以上は「+N 件」展開。
 */
import { Sparkles } from 'lucide-react';
import { useState } from 'react';

import type { DealTabKey, NextAction } from '@/hooks/useNextActions';

interface Props {
  actions: NextAction[];
  /** poId 指定時は、そのタブを開いたうえで該当の発注カードまでスクロールする。 */
  onJumpTab: (tab: DealTabKey, poId?: string) => void;
}

const PRIORITY_STYLE: Record<
  NextAction['priority'],
  { bg: string; text: string; border: string; chevron: string }
> = {
  critical: {
    bg: 'bg-[var(--accent-red-subtle)]',
    text: 'text-[var(--accent-red)]',
    border: 'border-[var(--accent-red)]',
    chevron: 'text-[var(--accent-red)]',
  },
  normal: {
    bg: 'bg-[var(--accent-amber-subtle)]',
    text: 'text-[var(--accent-amber)]',
    border: 'border-[var(--accent-amber)]',
    chevron: 'text-[var(--accent-amber)]',
  },
  info: {
    bg: 'bg-[var(--bg-elevated)]',
    text: 'text-[var(--text-secondary)]',
    border: 'border-[var(--border)]',
    chevron: 'text-[var(--text-muted)]',
  },
};

export function NextActionBar({ actions, onJumpTab }: Props) {
  const [expanded, setExpanded] = useState(false);

  if (actions.length === 0) return null;

  const visible = expanded ? actions : actions.slice(0, 3);
  const hidden = actions.length - visible.length;

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="size-3.5 text-[var(--accent)]" />
        <h3 className="text-xs font-semibold text-[var(--text-primary)]">次のアクション</h3>
        <span className="text-[10px] text-[var(--text-muted)]">
          {actions.length === 1
            ? '1 件'
            : `${actions.length} 件${hidden > 0 ? ` (${visible.length} 表示)` : ''}`}
        </span>
      </div>

      <div className="space-y-1.5">
        {visible.map((a) => {
          const s = PRIORITY_STYLE[a.priority];
          return (
            <button
              key={a.key}
              type="button"
              onClick={() => onJumpTab(a.targetTab, a.targetPoId)}
              className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-md border ${s.border} ${s.bg} hover:opacity-90 transition-opacity`}
            >
              <span className="text-base flex-shrink-0">{a.emoji}</span>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${s.text}`}>{a.label}</div>
                {a.hint && (
                  <div className="text-[11px] text-[var(--text-muted)] mt-0.5 truncate">
                    {a.hint}
                  </div>
                )}
              </div>
              <span className={`${s.chevron} text-xs flex-shrink-0`}>→</span>
            </button>
          );
        })}
      </div>

      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-2 text-[11px] text-[var(--accent)] hover:underline"
        >
          + {hidden} 件を表示
        </button>
      )}
      {expanded && actions.length > 3 && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="mt-2 text-[11px] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
        >
          折りたたむ
        </button>
      )}
    </div>
  );
}
